import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { BehaviorSubject, combineLatest, filter, map } from 'rxjs';
import { Job } from 'server/shared/models/job';
import { JobStatusEnum } from 'server/shared/models/jobStatusEnum';
import {
  isNonDefaultCustomCriterion,
  NonDefaultCustomCriteriaRow,
  toNonDefaultCustomCriteriaRow,
} from 'src/helpers/customCriteriaRowHelpers';
import { filterIsNotEmpty } from '../../../helpers/filterIsNotEmpty';
import { CustomerService } from '../../services/customer.service';
import { UserService } from '../../services/user.service';
import { TransferCounts } from '../job-list-item-transfers/job-list-item-transfers.component';

@Component({
  selector: 'app-dispo-job-list-item-details-table',
  templateUrl: './dispo-job-list-item-details-table.component.html',
  styleUrls: ['./dispo-job-list-item-details-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DispoJobListItemDetailsTableComponent {
  job$ = new BehaviorSubject<Job | undefined>(undefined);
  @Input() set job(job: Job | undefined) {
    this.job$.next(job);
  }
  get job(): Job | undefined {
    return this.job$.getValue();
  }

  @Input() transferCounts?: TransferCounts;

  @Input() transferredToMeText?: string;

  loggedInCustomer$ = this.customerService.readyDispoCustomer$;

  loggedInUser$ = this.userService.readyDispo$;

  isAgency$ = this.customerService.isAgency$;

  isOwnedOrganisationJob$ = combineLatest([
    this.loggedInCustomer$,
    this.job$.pipe(filter(filterIsNotEmpty)),
  ]).pipe(
    map(([ownCustomer, job]) => {
      return (
        ownCustomer?._id !== job.customer.id &&
        job.ownerCustomerIds?.some((owner) => owner === ownCustomer?._id)
      );
    }),
  );

  jobCertificates$ = this.job$.pipe(
    filter(filterIsNotEmpty),
    map((job) => {
      if (!job.certificates?.length) {
        return;
      }
      return job.certificates.map((c) => c.displayName).join(', ');
    }),
  );

  nonDefaultCustomCriteriaRows$ = combineLatest([
    this.loggedInCustomer$,
    this.job$.pipe(filter(filterIsNotEmpty)),
  ]).pipe(
    map(([loggedInCustomer, job]) => {
      const customerId = loggedInCustomer?._id;
      if (!customerId) {
        return [] as NonDefaultCustomCriteriaRow[];
      }
      const criteriaMap =
        job.customerInternalInformation?.[customerId]?.customCriteria;
      if (!criteriaMap) {
        return [];
      }
      return Object.values(criteriaMap)
        .filter(isNonDefaultCustomCriterion)
        .map(toNonDefaultCustomCriteriaRow);
    }),
  );

  constructor(
    private customerService: CustomerService,
    private userService: UserService,
  ) {}

  readonly JobStatusEnum = JobStatusEnum;

  trackByCustomCriteriaLabel(
    _index: number,
    row: NonDefaultCustomCriteriaRow,
  ): string {
    return row.label;
  }
}
